import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FOOTER_COLUMNS, TRUST_BADGES } from "@/data/content";
import { getFooter } from "@/lib/api";

const DEFAULT_STYLE = { height: 28, background: "none", monochrome: false, gap: 14 };

function LogoStrip({ title, logos, style, align = "flex-start" }) {
  const st = { ...DEFAULT_STYLE, ...(style || {}) };
  const chip =
    st.background === "light"
      ? "bg-white px-3 py-1.5 rounded-md shadow-sm"
      : st.background === "dark"
      ? "bg-surface border border-border px-3 py-1.5 rounded-md"
      : "";
  return (
    <div data-testid={`footer-strip-${title}`}>
      <div className="mono-label text-muted-foreground" style={{ textAlign: align === "flex-end" ? "right" : "left" }}>
        {title}
      </div>
      <div className="mt-4 flex flex-wrap items-center" style={{ gap: `${st.gap}px`, justifyContent: align }}>
        {logos.map((logo) => (
          <div key={logo.id} className={`flex items-center justify-center ${chip}`} title={logo.label || undefined} data-testid="footer-logo-chip">
            <img
              src={logo.image_url}
              alt={logo.label || "logo"}
              loading="lazy"
              className="w-auto max-w-[120px] object-contain"
              style={{ height: `${st.height}px`, filter: st.monochrome ? "brightness(0) invert(1)" : undefined }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const Dot = () => <span aria-hidden="true" className="text-border">·</span>;

export function Footer() {
  const [footer, setFooter] = useState({
    shipping_partners: [], payment_methods: [],
    shipping_style: DEFAULT_STYLE, payment_style: DEFAULT_STYLE,
  });

  useEffect(() => {
    let active = true;
    getFooter()
      .then((d) => {
        if (active && d) setFooter({
          shipping_partners: d.shipping_partners || [],
          payment_methods: d.payment_methods || [],
          shipping_style: d.shipping_style || DEFAULT_STYLE,
          payment_style: d.payment_style || DEFAULT_STYLE,
        });
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const hasShipping = footer.shipping_partners.length > 0;
  const hasPayments = footer.payment_methods.length > 0;

  return (
    <footer className="border-t border-border bg-ink" data-testid="site-footer">
      <div className="container-page pb-10 pt-20">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-center font-display text-2xl font-bold tracking-tightest text-foreground" aria-label="RINOVA home">
              RINOVA
              <span className="ml-1.5 inline-block h-1.5 w-1.5 -translate-y-2.5 rounded-[1px] bg-lime" aria-hidden="true" />
            </Link>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-muted-foreground">
              Tecnologia nuova e ricondizionata, verificata in ogni componente e garantita 12 mesi. Trasparenza totale sulle condizioni, sempre.
            </p>
            <ul className="mt-7 space-y-2.5">
              {TRUST_BADGES.map((t) => (
                <li key={t.key} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-[1px] bg-lime" aria-hidden="true" />
                  <span>{t.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7 lg:col-start-6">
            {FOOTER_COLUMNS.map((col) => (
              <div key={col.title}>
                <div className="mono-label text-muted-foreground">{col.title}</div>
                <ul className="mt-5 space-y-3">
                  {col.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link to={l.to} className="text-sm text-foreground/80 transition-colors duration-200 hover:text-lime">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Partner di spedizione + metodi di pagamento (loghi gestiti da admin) */}
        {(hasShipping || hasPayments) && (
          <div className="mt-16 border-t border-border pt-10" data-testid="footer-logos">
            <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
              {hasShipping ? (
                <LogoStrip title="Spedizioni" logos={footer.shipping_partners} style={footer.shipping_style} />
              ) : <span />}
              {hasPayments && (
                <LogoStrip title="Pagamenti sicuri" logos={footer.payment_methods} style={footer.payment_style} align="flex-end" />
              )}
            </div>
          </div>
        )}

        {/* Dati legali societari — stile minimalista, righe compatte */}
        <div className="mt-14 border-t border-border pt-6 text-xs leading-relaxed text-muted-foreground" data-testid="footer-legal">
          <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="font-medium text-foreground/80">NOVA STRATEGIE SRLS – Società a responsabilità limitata semplificata unipersonale</span> <Dot />
            <span>P.IVA e C.F. 09187850723</span> <Dot />
            <span>REA BA-677945</span> <Dot />
            <span>PEC: novastrategiesrls@pec.it</span> <Dot />
            <span>Sede legale: Via San Lorenzo 40, 76011 Bisceglie (BT)</span>
          </p>
          <p className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <a href="mailto:info@rinovotech.it" className="transition-colors duration-200 hover:text-lime" data-testid="footer-email">info@rinovotech.it</a> <Dot />
            <a href="tel:+393893670190" className="transition-colors duration-200 hover:text-lime" data-testid="footer-phone">+39 389 367 0190</a> <Dot />
            <span>© 2024–2026 NOVA STRATEGIE SRLS · Tutti i diritti riservati</span> <Dot />
            <span>Note legali</span> <Dot />
            <span>Privacy</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
